import axios from 'axios';
import { getBaseUrl } from 'utils/url';
import {
  StripeSubscriptionPlan,
  StripeSubscription,
  BackendSubscription,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  StripePaymentMethod,
  SubscriptionDiscountPreview,
} from 'types/stripe';

class StripeService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getBaseUrl();
  }

  private getHeaders(token?: string) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  // Get available subscription plans
  async getSubscriptionPlans(
    token?: string,
  ): Promise<StripeSubscriptionPlan[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/api/stripe/plans`, {
        headers: this.getHeaders(token),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      throw error;
    }
  }

  /** Preview discount for a plan + promotion code (requires backend; uses Stripe upcoming invoice). */
  async previewSubscriptionDiscount(
    body: { planId: string; promotionCode: string },
    token?: string,
    signal?: AbortSignal,
  ): Promise<SubscriptionDiscountPreview> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/stripe/subscriptions/preview`,
        body,
        { headers: this.getHeaders(token), signal },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }
      console.error('Error previewing subscription discount:', error);
      throw error;
    }
  }

  // Create a new subscription
  async createSubscription(
    request: CreateSubscriptionRequest,
    token?: string,
  ): Promise<CreateSubscriptionResponse> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/stripe/subscriptions`,
        request,
        { headers: this.getHeaders(token) },
      );
      return response.data;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  // Get customer's subscriptions (now handles both data structures)
  async getCustomerSubscriptions(
    token?: string,
  ): Promise<(StripeSubscription | BackendSubscription)[]> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/api/stripe/subscriptions`,
        { headers: this.getHeaders(token) },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching customer subscriptions:', error);
      throw error;
    }
  }

  // Cancel a subscription
  async cancelSubscription(
    subscriptionId: string,
    token?: string,
  ): Promise<void> {
    try {
      await axios.delete(
        `${this.baseUrl}/api/stripe/subscriptions/${subscriptionId}`,
        { headers: this.getHeaders(token) },
      );
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  // Get customer's payment methods
  async getPaymentMethods(token?: string): Promise<StripePaymentMethod[]> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/api/stripe/payment-methods`,
        { headers: this.getHeaders(token) },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      throw error;
    }
  }

  // Add a new payment method
  async addPaymentMethod(
    paymentMethodId: string,
    token?: string,
  ): Promise<StripePaymentMethod> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/stripe/payment-methods`,
        { paymentMethodId },
        { headers: this.getHeaders(token) },
      );
      return response.data;
    } catch (error) {
      console.error('Error adding payment method:', error);
      throw error;
    }
  }

  // Remove a payment method
  async removePaymentMethod(
    paymentMethodId: string,
    token?: string,
  ): Promise<void> {
    try {
      await axios.delete(
        `${this.baseUrl}/api/stripe/payment-methods/${paymentMethodId}`,
        { headers: this.getHeaders(token) },
      );
    } catch (error) {
      console.error('Error removing payment method:', error);
      throw error;
    }
  }

  // Update subscription
  async updateSubscription(
    subscriptionId: string,
    paymentMethodId: string,
    token?: string,
  ): Promise<StripeSubscription> {
    try {
      const response = await axios.put(
        `${this.baseUrl}/api/stripe/subscriptions/${subscriptionId}`,
        { paymentMethodId },
        { headers: this.getHeaders(token) },
      );
      return response.data;
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  // Create a Setup Intent for adding payment methods
  async createSetupIntent(token?: string): Promise<{ clientSecret: string }> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/stripe/setup-intents`,
        {},
        { headers: this.getHeaders(token) },
      );
      return response.data;
    } catch (error) {
      console.error('Error creating setup intent:', error);
      throw error;
    }
  }

  // Create a Payment Intent for purchases
  async createPaymentIntent(
    request: {
      amount: number;
      currency: string;
    },
    token?: string,
  ): Promise<{ client_secret: string }> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/stripe/payment-intents`,
        request,
        { headers: this.getHeaders(token) },
      );

      return response.data;
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  }
}

export const stripeService = new StripeService();
